import { useState } from "react";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useExperienceSections, useAdminExperienceSections } from "@/hooks/useExperienceSections";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAdventPassStatus } from "@/hooks/useAdventPassStatus";
import { useAdventPassEligibility } from "@/hooks/useAdventPassEligibility";
import EventsEmptyState from "@/components/event/EventsEmptyState";
import DynamicEventSection from "@/components/experience/DynamicEventSection";
import ExperienceSectionEditor from "@/components/experience/ExperienceSectionEditor";
import ExperienceSectionDialog from "@/components/experience/ExperienceSectionDialog";
import AddSectionButton from "@/components/experience/AddSectionButton";
import { ExperienceSection } from "@/types/experience-section";
import { Event } from "@/types/event";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import CreateEventForm from "@/components/admin/CreateEventForm";

type UserEventListProps = {
  selectedTag: string;
  userId: string;
  availableTags: string[];
  onSelectTag: (tag: string) => void;
  isFromBingo: boolean;
};

const UserEventList = (props: UserEventListProps) => {
  const { selectedTag, userId, availableTags, onSelectTag, isFromBingo } = props;
  const { isAdmin } = useIsAdmin();
  const queryClient = useQueryClient();
  
  const [editingSection, setEditingSection] = useState<ExperienceSection | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  // Use admin hook if admin, regular hook otherwise
  const adminSections = useAdminExperienceSections();
  const regularSections = useExperienceSections();
  
  const { sections, isLoading: isLoadingSections } = isAdmin ? adminSections : regularSections;
  const { createSection, updateSection, deleteSection, reorderSections } = adminSections;

  // Fetch booking status for all events
  const { data: bookedEventIds } = useQuery({
    queryKey: ['booked-events', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('tickets')
        .select('event_id')
        .eq('user_id', userId)
        .eq('status', 'confirmed');
      return data?.map(ticket => ticket.event_id) || [];
    },
  });

  // Fetch Advent Pass status
  const { data: adventPassStatus } = useAdventPassStatus();
  const { data: adventPassEligibility } = useAdventPassEligibility();

  // Fetch waitlist status
  const { data: waitlistEventIds } = useQuery({
    queryKey: ['user-waitlist-events', userId],
    queryFn: async () => {
      const { data } = await supabase
        .from('salon_waitlist')
        .select('event_id')
        .eq('user_id', userId)
        .eq('status', 'pending');
      return data?.map(item => item.event_id) || [];
    },
  });

  // Prepare Advent Pass data
  const adventPassData = adventPassStatus?.hasAdventPass && adventPassStatus.partnerCode ? {
    hasPass: true,
    validFrom: adventPassStatus.partnerCode.valid_from ? new Date(adventPassStatus.partnerCode.valid_from) : null,
    validUntil: adventPassStatus.partnerCode.valid_until ? new Date(adventPassStatus.partnerCode.valid_until) : null,
    discountPercentage: adventPassStatus.partnerCode.discount_percentage || 0,
  } : null;

  const handleMoveUp = (id: string) => {
    const index = sections.findIndex(s => s.id === id);
    if (index > 0) {
      const newOrder = [...sections];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      reorderSections.mutate(newOrder.map(s => s.id));
    }
  };

  const handleMoveDown = (id: string) => {
    const index = sections.findIndex(s => s.id === id);
    if (index < sections.length - 1) {
      const newOrder = [...sections];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      reorderSections.mutate(newOrder.map(s => s.id));
    }
  };

  const handleEdit = (section: ExperienceSection) => {
    setEditingSection(section);
    setIsCreating(false);
    setIsDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingSection(null);
    setIsCreating(true);
    setIsDialogOpen(true);
  };

  const handleSave = (data: Partial<ExperienceSection>) => {
    if (isCreating) {
      const maxOrder = Math.max(...sections.map(s => s.display_order), 0);
      createSection.mutate({ 
        ...data as any, 
        display_order: maxOrder + 1 
      });
    } else if (data.id) {
      updateSection.mutate(data as ExperienceSection & { id: string });
    }
    setIsDialogOpen(false);
    setEditingSection(null);
  };

  const handleDelete = (id: string) => {
    deleteSection.mutate(id);
  };

  if (isLoadingSections) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex min-h-44 overflow-hidden rounded-lg bg-card shadow-md">
            <div className="w-28 shrink-0 animate-pulse bg-muted sm:w-40" />
            <div className="min-w-0 flex-1 space-y-3 p-4 sm:p-5">
              <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
              <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Filter sections based on isFromBingo
  const visibleSections = isFromBingo 
    ? sections.filter(s => s.section_type === 'tag_based')
    : sections;

  return (
    <div className="relative">
      <div className="space-y-6">
        {visibleSections.map((section, index) => (
          <ExperienceSectionEditor
            key={section.id}
            section={section}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onMoveUp={handleMoveUp}
            onMoveDown={handleMoveDown}
            isAdmin={isAdmin || false}
            isFirst={index === 0}
            isLast={index === visibleSections.length - 1}
            orderNumber={index + 1}
          >
            <DynamicEventSection
              section={section}
              userId={userId}
              bookedEventIds={bookedEventIds || []}
              adventPassEligibility={adventPassEligibility}
              adventPassData={adventPassData}
              isFromBingo={isFromBingo}
              isAdmin={isAdmin || false}
              showSeparator={index > 0}
              onEditEvent={isAdmin ? (event) => setEditingEvent(event) : undefined}
            />
          </ExperienceSectionEditor>
        ))}

        {visibleSections.length === 0 && (
          <EventsEmptyState tag={selectedTag} />
        )}

        {isAdmin && (
          <AddSectionButton onClick={handleCreate} />
        )}
      </div>

      <ExperienceSectionDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        section={editingSection}
        onSave={handleSave}
        isLoading={createSection.isPending || updateSection.isPending}
      />

      <Dialog open={!!editingEvent} onOpenChange={() => setEditingEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0" hideCloseButton>
          {editingEvent && (
            <CreateEventForm 
              event={editingEvent} 
              onClose={() => {
                setEditingEvent(null);
                queryClient.invalidateQueries({ queryKey: ['section-events'] });
              }} 
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserEventList;
